import {
  Button,
  Checkbox,
  CountBadge,
  Css,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  Palette,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { ReactNode, useMemo, useState } from "react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import {
  EditPersonalizationOptionWizard_OptionCombinationFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import {
  FormValue,
  PackageOption,
  ProductOption,
  removeDuplicateCombinations,
} from "src/routes/libraries/design-catalog/personalization-option/utils";

type CombinationsRow = ObjectState<ProductOption> | ObjectState<PackageOption>;

type CombinationsLinksProps<T extends CombinationsRow> = {
  formState: ObjectState<FormValue>;
  row: T;
  allRows: readonly T[];
  /** Names an option next to the combinations it is already the default for. */
  getRowName: (row: T) => string;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
};

/** An option's "Default In" and "Available In" links, which open the modal to edit its combinations. */
export function CombinationsLinks<T extends CombinationsRow>(props: CombinationsLinksProps<T>) {
  const { formState, row, allRows, getRowName, targetGroups, targetCombinations } = props;
  const { openModal } = useModal();
  return (
    <Observer>
      {() => {
        // Single product defaults are set on the regular placeholder's product cards instead of on its options
        const showDefaultIn = formState.type.value !== ReadyPlanOptionGroupPersonalizationType.SingleProduct;
        const showAvailableIn = !!formState.enableCuration.value;
        if (!showDefaultIn && !showAvailableIn) return;
        const groupNames = targetGroups
          .filter((g) => formState.personalizationTargetGroupIds.value?.includes(g.id))
          .map((g) => g.name);
        const disabled = targetCombinations.isEmpty && "Select target groups under Apply To first.";
        function openCombinationsModal() {
          openModal({
            content: (
              <CombinationsModal
                row={row}
                allRows={allRows}
                getRowName={getRowName}
                groupNames={groupNames}
                targetCombinations={targetCombinations}
                showDefaultIn={showDefaultIn}
                showAvailableIn={showAvailableIn}
                readOnly={formState.readOnly}
              />
            ),
          });
        }
        return (
          <div css={Css.df.aic.gap1.$}>
            {showDefaultIn && (
              <CombinationsLink
                label="Default In"
                combinations={row.personalizationTargetCombinations.value}
                targetCombinations={targetCombinations}
                disabled={disabled}
                onClick={openCombinationsModal}
              />
            )}
            {showAvailableIn && (
              <CombinationsLink
                label="Available In"
                combinations={row.curationTargetCombinations.value}
                targetCombinations={targetCombinations}
                disabled={disabled}
                onClick={openCombinationsModal}
              />
            )}
          </div>
        );
      }}
    </Observer>
  );
}

type CombinationsLinkProps = {
  label: string;
  combinations: { options: string[] }[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  disabled: ReactNode;
  onClick: () => void;
};

function CombinationsLink(props: CombinationsLinkProps) {
  const { label, combinations, targetCombinations, disabled, onClick } = props;
  const names = combinations
    .map((combo) => targetCombinations.find((tc) => tc.id === combo.options.join())?.name)
    .compact();
  const tooltip = names.nonEmpty ? names.map((name) => <div key={name}>{name}</div>) : undefined;
  return (
    <Button
      variant="text"
      label={label}
      endAdornment={<CountBadge count={combinations.length} />}
      tooltip={tooltip}
      disabled={disabled}
      onClick={onClick}
    />
  );
}

type CombinationsModalProps<T extends CombinationsRow> = {
  row: T;
  allRows: readonly T[];
  getRowName: (row: T) => string;
  groupNames: string[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  showDefaultIn: boolean;
  showAvailableIn: boolean;
  readOnly: boolean;
};

/** Stages an option's default and curation combinations, and only applies them on save. */
function CombinationsModal<T extends CombinationsRow>(props: CombinationsModalProps<T>) {
  const { row, allRows, getRowName, groupNames, targetCombinations, showDefaultIn, showAvailableIn, readOnly } = props;
  const tid = useTestIds(props, "combinationsModal");
  const { closeModal } = useModal();
  const [defaultIn, setDefaultIn] = useState(() =>
    row.personalizationTargetCombinations.value.map((c) => c.options.join()),
  );
  const [availableIn, setAvailableIn] = useState(() =>
    row.curationTargetCombinations.value.map((c) => c.options.join()),
  );
  const otherDefaults = useMemo(
    () =>
      new Map(
        allRows
          .filter((r) => r !== row)
          .flatMap((r) => r.personalizationTargetCombinations.value.map((c) => [c.options.join(), getRowName(r)])),
      ),
    [row, allRows, getRowName],
  );

  function save() {
    if (showDefaultIn) {
      // Default combinations can't overlap, so selecting another option's default moves it to this option
      removeDuplicateCombinations(row, allRows, defaultIn);
      row.personalizationTargetCombinations.set(defaultIn.map((key) => ({ options: key.split(",") })));
    }
    if (showAvailableIn) row.curationTargetCombinations.set(availableIn.map((key) => ({ options: key.split(",") })));
    closeModal();
  }

  return (
    <>
      <ModalHeader>Set {groupNames.join(" + ")} Availability in Checkout</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <p css={Css.sm.gray700.$}>Select the combinations this option applies to in Checkout.</p>
          {showDefaultIn && (
            <CombinationsSelect
              label="Default In"
              helperText="The buyer will automatically see this option selected for these combinations."
              values={defaultIn}
              onSelect={setDefaultIn}
              targetCombinations={targetCombinations}
              readOnly={readOnly}
              getWarning={(key) => {
                const otherRowName = otherDefaults.get(key);
                return otherRowName !== undefined ? `Default for "${otherRowName}"` : undefined;
              }}
              {...tid.defaultIn}
            />
          )}
          {showAvailableIn && (
            <CombinationsSelect
              label="Available In"
              helperText="The buyer can choose this option as an alternative for these combinations."
              values={availableIn}
              onSelect={setAvailableIn}
              targetCombinations={targetCombinations}
              readOnly={readOnly}
              {...tid.availableIn}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button {...tid.cancel} variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button {...tid.save} label="Save Availability" disabled={readOnly} onClick={save} />
      </ModalFooter>
    </>
  );
}

type CombinationsSelectProps = {
  label: string;
  helperText: string;
  values: string[];
  onSelect: (values: string[]) => void;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  readOnly: boolean;
  /** A warning to show next to a combination in the menu */
  getWarning?: (key: string) => string | undefined;
};

function CombinationsSelect(props: CombinationsSelectProps) {
  const { label, helperText, values, onSelect, targetCombinations, readOnly, getWarning } = props;
  const tid = useTestIds(props);
  const allSelected = targetCombinations.nonEmpty && values.length === targetCombinations.length;
  return (
    <div css={Css.df.fdc.gap2.$}>
      <MultiSelectField
        {...tid}
        label={label}
        helperText={helperText}
        options={targetCombinations}
        getOptionValue={(c) => c.id}
        getOptionLabel={(c) => c.name}
        getOptionMenuLabel={(c) => {
          const warning = getWarning?.(c.id);
          if (!warning) return c.name;
          return (
            <div css={Css.df.aifs.gap1.$}>
              {c.name}
              <Icon icon="error" color={Palette.Yellow600} inc={2} tooltip={warning} />
            </div>
          );
        }}
        values={values}
        onSelect={onSelect}
        placeholder="Select Option Combinations"
        readOnly={readOnly}
      />
      <Checkbox
        {...tid.selectAll}
        label="Select All"
        selected={allSelected}
        disabled={readOnly}
        onChange={(selected) => onSelect(selected ? targetCombinations.map((c) => c.id) : [])}
      />
    </div>
  );
}
